import React from 'react';

export type TableSize = 'default' | 'small';

export interface TableColumn<T> {
  key: keyof T | string;
  header: string;
  numeric?: boolean;
  render?: (item: T, index: number) => React.ReactNode;
}

export interface TableProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  caption?: string;
  size?: TableSize;
  striped?: boolean;
  responsive?: boolean;
  keyExtractor?: (item: T, index: number) => string | number;
  keyField?: keyof T;
  onRowClick?: (item: T, index: number) => void;
  className?: string;
  rowClassName?: (item: T, index: number) => string;
  expandedRowPredicate?: (item: T, index: number) => boolean;
  expandedRowContent?: (item: T, index: number) => React.ReactNode;
}

/**
 * KERN Table Component
 *
 * Usage:
 * <Table
 *   caption="Benutzer"
 *   data={users}
 *   columns={[
 *     { key: 'name', header: 'Name' },
 *     { key: 'email', header: 'E-Mail' },
 *     { key: 'actions', header: 'Aktionen', render: (user) => <Button>Edit</Button> }
 *   ]}
 *   striped
 * />
 */
export function Table<T extends object>({
  data,
  columns,
  caption,
  size = 'default',
  striped = false,
  responsive = false,
  keyExtractor,
  keyField,
  onRowClick,
  className = '',
  rowClassName,
  expandedRowPredicate,
  expandedRowContent,
}: TableProps<T>) {
  const getRowKey = (item: T, index: number): string | number => {
    if (keyExtractor) return keyExtractor(item, index);
    if (keyField) return String(item[keyField]);
    return index;
  };
  const tableClasses = [
    'kern-table',
    size === 'small' && 'kern-table--small',
    striped && 'kern-table--striped',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const getCellValue = (item: T, column: TableColumn<T>): React.ReactNode => {
    if (column.render) {
      return column.render(item, data.indexOf(item));
    }
    const value = item[column.key as keyof T];
    return value as React.ReactNode;
  };

  const tableContent = (
    <table className={tableClasses}>
      {caption && <caption className="kern-title">{caption}</caption>}
      <thead className="kern-table__head">
        <tr className="kern-table__row">
          {columns.map((column) => (
            <th
              key={String(column.key)}
              scope="col"
              className={`kern-table__header${column.numeric ? ' kern-table__header--numeric' : ''}`}
            >
              {column.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="kern-table__body">
        {data.map((item, index) => {
          const isExpanded = expandedRowPredicate ? expandedRowPredicate(item, index) : false;
          const expandedContent =
            isExpanded && expandedRowContent ? expandedRowContent(item, index) : null;

          return (
            <React.Fragment key={getRowKey(item, index)}>
              <tr
                className={`kern-table__row${rowClassName ? ' ' + rowClassName(item, index) : ''}`}
                onClick={onRowClick ? () => onRowClick(item, index) : undefined}
                style={onRowClick ? { cursor: 'pointer' } : undefined}
              >
                {columns.map((column, colIndex) => {
                  const isFirstColumn = colIndex === 0;
                  const CellTag = isFirstColumn ? 'th' : 'td';
                  const cellClass = isFirstColumn
                    ? 'kern-table__header'
                    : `kern-table__cell${column.numeric ? ' kern-table__cell--numeric' : ''}`;

                  return (
                    <CellTag
                      key={String(column.key)}
                      scope={isFirstColumn ? 'row' : undefined}
                      className={cellClass}
                    >
                      {getCellValue(item, column)}
                    </CellTag>
                  );
                })}
              </tr>

              {expandedContent && (
                <tr className="kern-table__row kern-table__row--expanded" aria-expanded="true">
                  <td
                    className="kern-table__cell kern-table__cell--expanded"
                    colSpan={columns.length}
                  >
                    {expandedContent}
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );

  if (responsive) {
    return (
      <div className="kern-table-responsive" tabIndex={0}>
        {tableContent}
      </div>
    );
  }

  return tableContent;
}

export default Table;
